import { LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import globalStyles from './nuvance-location-watcher.global.pcss?inline';
import CookieHelper from '../../utilities/cookie-helper';

/**
 * Location data interface
 */
interface LocationData {
  name: string;
  location: {
    lat: number;
    lng: number;
  };
  isCurrentLocation: boolean;
}

/**
 * The Nuvance Location Watcher component
 *
 * This component watches for location changes and updates content fetch components
 * with the user's location data. It reads from the location cookie and listens for
 * location change events from nuvance-location-set.
 *
 * @element nuvance-location-watcher
 *
 * @property {string} targetSelector - Query selector for nuvance-content-fetch components to update
 * @property {boolean} debug - Enable debug logging
 *
 * @listens preferred-location-changed - Event emitted when a location is set or updated
 *
 * Example working kyruus API endpoint:
 * /api/kyruus/provider-meet-the-team?sort=distance&location=41.411181%2C-72.9279&distance=25&limit=10
 */
@customElement('nuvance-location-watcher')
export class NuvanceLocationWatcher extends LitElement {
  /**
   * Adopted stylesheets controller
   */
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
  });

  /**
   * The query selector to find nuvance-content-fetch components to update
   */
  @property({ type: String, attribute: 'target-selector' })
  targetSelector = 'nuvance-content-fetch';

  /**
   * Debug mode flag that enables logging of component operations to help with troubleshooting
   */
  @property({ type: Boolean, reflect: true })
  debug = false;

  /**
   * Bound handler reference for event listener cleanup
   */
  private boundHandleLocationChange: EventListener;

  constructor() {
    super();
    // Create a bound reference to the event handler for adding/removing
    this.boundHandleLocationChange = this.handleLocationChange.bind(
      this
    ) as EventListener;
  }

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  /**
   * Set up event listeners and perform initial update when connected to DOM
   */
  connectedCallback(): void {
    super.connectedCallback();

    // Update content fetch components with existing location cookie data
    this.updateContentFetchComponents();

    // Listen for location change events
    document.addEventListener(
      'preferred-location-changed',
      this.boundHandleLocationChange
    );

    this.debugLog('Location watcher initialized', {
      targetSelector: this.targetSelector,
    });
  }

  /**
   * Remove event listeners when disconnected from DOM
   */
  disconnectedCallback(): void {
    document.removeEventListener(
      'preferred-location-changed',
      this.boundHandleLocationChange
    );
    super.disconnectedCallback();
  }

  /**
   * Handle location change events
   */
  private handleLocationChange(event: Event): void {
    const customEvent = event as CustomEvent<LocationData>;
    const locationData = customEvent.detail;
    this.debugLog('Location change event received', locationData);
    this.updateContentFetchComponents();
  }

  /**
   * Update all matching content fetch components with location data
   */
  private updateContentFetchComponents(): void {
    const locationData = this.getLocationData();

    if (!locationData) {
      this.debugLog('No location data available');
      return;
    }

    const contentFetchElements = document.querySelectorAll(this.targetSelector);
    this.debugLog(
      `Found ${contentFetchElements.length} content fetch elements`,
      { selector: this.targetSelector }
    );

    contentFetchElements.forEach((element, index) => {
      this.updateContentFetchPath(element, locationData, index);
    });
  }

  /**
   * Update the path attribute of a content fetch element with location data
   */
  private updateContentFetchPath(
    element: Element,
    locationData: LocationData,
    index: number
  ): void {
    const currentPath = element.getAttribute('path');

    if (!currentPath) {
      this.debugLog(`Content fetch element ${index} has no path attribute`);
      return;
    }

    const { lat, lng } = locationData.location;
    const locationParam = `${lat},${lng}`;
    const newPath = this.updateLocationParam(currentPath, locationParam);

    this.debugLog(`Updating path for element ${index}`, {
      oldPath: currentPath,
      newPath: newPath,
    });

    element.setAttribute('path', newPath);
  }

  /**
   * Update or add the location parameter in a URL path
   */
  private updateLocationParam(path: string, locationValue: string): string {
    // Split the path into base path and query string
    const [basePath, queryString] = path.split('?');

    // Parse existing query parameters into an object
    const params: Record<string, string> = {};
    if (queryString) {
      queryString.split('&').forEach(param => {
        const [key, value] = param.split('=');
        if (key) {
          params[key] = value || '';
        }
      });
    }

    // Set the location parameter with the comma replaced by %2C
    const formattedLocation = locationValue.replace(',', '%2C');
    params['location'] = formattedLocation;

    // Build the new query string
    const newQueryString = Object.entries(params)
      .map(([key, value]) => `${key}=${value}`)
      .join('&');

    // Combine base path with new query string
    return `${basePath}?${newQueryString}`;
  }

  /**
   * Get location data from the cookie
   */
  private getLocationData(): LocationData | null {
    try {
      const cookieValue = CookieHelper.getCookie('nuvance-preferred-location');

      if (!cookieValue) {
        this.debugLog('No location cookie found');
        return null;
      }

      const locationData = JSON.parse(cookieValue) as LocationData;

      if (
        !locationData.location ||
        typeof locationData.location.lat !== 'number' ||
        typeof locationData.location.lng !== 'number'
      ) {
        this.debugLog('Invalid location data in cookie', locationData);
        return null;
      }

      this.debugLog('Retrieved location data from cookie', locationData);
      return locationData;
    } catch (error) {
      this.debugLog('Error parsing location cookie', error);
      return null;
    }
  }

  /**
   * Utility method for debug logging
   * Only logs when debug mode is enabled
   */
  private debugLog(message: string, ...args: unknown[]): void {
    if (this.debug) {
      console.log(`[nuvance-location-watcher] ${message}`, ...args);
    }
  }

  /**
   * This component doesn't render any visible content
   */
  render() {
    return nothing;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nuvance-location-watcher': NuvanceLocationWatcher;
  }
}
